import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useAuthStore from '@/stores/useAuthStore'
import { formatPGDate } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Service } from '@/modules/Service/types'
import { MEETING_TICKET_KEY } from '../../constants'
import { createTicket } from '../../services'
import {
    TicketAttachment,
    TicketType as TicketTypeResponse,
} from '../../types/response'
import useUploadFile from '../../types/uploadfile'
import { TICKET_ATTACHMENT_FILE_PREFIX, TicketUrgency } from '../../types/utils'
import { useTicketTemplate } from '../../templates'
import { createTicketSchema } from './schema'
import {
    CreateTicketFormValues,
    EditorValue,
    SLAInfo,
    ServiceSLAData,
} from './types'

export const useCreateTicketForm = () => {
    const { employee } = useAuthStore()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    // Track SLA info for display
    const [slaInfo, setSlaInfo] = useState<SLAInfo>({})

    // Track service SLA for time calculation
    const [serviceSLA, setServiceSLA] = useState<ServiceSLAData>({})

    const formControl = useForm<CreateTicketFormValues>({
        defaultValues: {
            ticket_priority: TicketUrgency(t)?.[0]?.value || '',
            ticket_type_id: 0,
            ticket_type_group: '',
            ticket_content: '',
            ticket_requested_at: dayjs(),
            employee_id: employee?.employee_id || -1,
            ticket_technical_assignees: [],
            ticket_cs_assignees: [],
            ticket_sale_assignees: [],
            ticket_estimated_completion_date: dayjs() || null,
            customer_id: 0,
            service_id: 0,
            ticket_affected: '',
            ticket_time_repply: dayjs(),
            service_startdate: null,
            service_duedate: null,
            service_suspend_until: null,
            ticket_attachment: [],
            ticket_meeting_type: 'online',
            ticket_meeting_via: '',
            ticket_marjorable: false,
            ticket_security_tag: 'none',
            ticket_product_name: '',
            ticket_meeting_purpose: '',
            customer_journey_id:
                Number(searchParams.get('customer_journey_id')) || undefined,
            include_downtime: false,
            downtime_start: null,
            downtime_end: null,
            downtime_cause: 'provider',
            downtime_counts_against_sla: true,
        },
        // @ts-ignore
        resolver: yupResolver(createTicketSchema(t)),
    })

    const { control, handleSubmit, watch, setValue } = formControl

    // Template system
    const { getDefaults, getSampleContent } = useTicketTemplate(
        watch('ticket_type_group') || '',
        watch('ticket_type_key')
    )

    const { uploadAsync } = useUploadFile({
        prefix: TICKET_ATTACHMENT_FILE_PREFIX,
        isPublic: true,
        onError: () => toast.error(t('upload-file-failed')),
    })

    const { mutateAsync: createTicketMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: createTicket },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                onSuccess: (data) => {
                    const ticketId =
                        'result' in data ? data.result?.ticket_id : undefined
                    if (ticketId) {
                        navigate(
                            generatePathDatacentral(paths.ticketDetail, {
                                id: ticketId,
                            })
                        )
                    } else {
                        navigate('/tickets')
                    }
                },
            }
        )

    // Extract text helper
    const extractText = (field: unknown): string => {
        if (typeof field === 'object' && field !== null && 'text' in field) {
            return (field as { text: string }).text || ''
        }
        return typeof field === 'string' ? field : ''
    }

    const onSubmit = async (data: CreateTicketFormValues) => {
        const contentValue = data.ticket_content as unknown as EditorValue
        const contentText =
            typeof contentValue === 'object'
                ? contentValue.text
                : (contentValue as string) || ''

        const editorAttachments = Array.isArray(contentValue?.attachments)
            ? contentValue.attachments
            : []

        let uploadedAttachments: TicketAttachment[] = []
        if (editorAttachments.length > 0) {
            const files = editorAttachments.map((a) => a.file)
            const results = await uploadAsync(files)
            uploadedAttachments = results
                .filter((r: any) => r.status === 'fulfilled')
                .map((r: any) => r.value as TicketAttachment)
        }

        const isErrorService = data.ticket_type_group === 'error_service'
        const includeDowntime = isErrorService && data.include_downtime
        const downtimePayload =
            includeDowntime && data.downtime_start
                ? {
                      downtime_type: 'unplanned' as const,
                      downtime_start: dayjs(data.downtime_start).toISOString(),
                      downtime_end: data.downtime_end
                          ? dayjs(data.downtime_end).toISOString()
                          : undefined,
                      downtime_cause: (data.downtime_cause || 'provider') as
                          | 'provider'
                          | 'customer'
                          | 'third_party'
                          | 'force_majeure'
                          | 'scheduled',
                      downtime_description: extractText(data?.ticket_affected),
                      counts_against_sla: data.downtime_counts_against_sla,
                  }
                : undefined

        const dataToSubmit = {
            payload: {
                note: {
                    ...data,
                    include_downtime: undefined,
                    downtime_start: undefined,
                    downtime_end: undefined,
                    downtime_cause: undefined,
                    downtime_counts_against_sla: undefined,
                    service_startdate: dayjs(data.service_startdate).isValid()
                        ? dayjs(data.service_startdate).format('YYYY-MM-DD')
                        : null,
                    service_duedate: dayjs(data.service_duedate).isValid()
                        ? dayjs(data.service_duedate).format('YYYY-MM-DD')
                        : null,
                    service_suspend_until: dayjs(
                        data.service_suspend_until
                    ).isValid()
                        ? dayjs(data.service_suspend_until).format('YYYY-MM-DD')
                        : null,
                    ticket_content: contentText,
                    ticket_root_cause: extractText(data?.ticket_root_cause),
                    ticket_remedial_measures: extractText(
                        data?.ticket_remedial_measures
                    ),
                    ticket_avoid: extractText(data?.ticket_avoid),
                    ticket_affected: extractText(data?.ticket_affected),
                    ticket_marjorable:
                        data?.ticket_type_id !== 2
                            ? false
                            : data?.ticket_marjorable,
                },
                attachments: uploadedAttachments,
                downtime: downtimePayload,
            },
        }

        try {
            await createTicketMutation(dataToSubmit)
        } catch (error) {
            console.error('Error submitting form', error)
        }
    }

    // Handle service change
    const handleServiceChange = (
        service:
            | Partial<
                  Service & {
                      type: 'service'
                      label: string
                      value: number
                      cs_assignees?: number[]
                      sale_assignees?: number[]
                  }
              >
            | {
                  type: 'service_interesting'
                  label: string
                  value: string
                  cs_assignees?: number[]
                  sale_assignees?: number[]
              }
    ) => {
        setServiceSLA({})

        if (service.type === 'service') {
            setValue('service_id', service.service_id)
            if (service.service_start_date) {
                setValue(
                    'service_startdate',
                    formatPGDate(service?.service_start_date) as string
                )
            }
            if (service.service_stopped_date) {
                setValue(
                    'service_duedate',
                    formatPGDate(service?.service_stopped_date) as string
                )
            }
        } else {
            setValue('ticket_product_name', service.label)
            setValue('service_id', 0)
            setValue('service_startdate', null)
            setValue('service_duedate', null)
        }
        if (service.cs_assignees && service.cs_assignees.length > 0) {
            setValue('ticket_cs_assignees', [...new Set(service.cs_assignees)])
        }
        if (service.sale_assignees && service.sale_assignees.length > 0) {
            setValue('ticket_sale_assignees', [
                ...new Set(service.sale_assignees),
            ])
        }
    }

    // Handle SLA loaded from service
    const handleServiceSLALoaded = useCallback(
        (slaData: ServiceSLAData) => {
            setServiceSLA(slaData)

            const requestedAt = watch('ticket_requested_at') || dayjs()

            if (slaData.responseTimeMinutes) {
                setValue(
                    'ticket_time_repply',
                    dayjs(requestedAt).add(
                        slaData.responseTimeMinutes,
                        'minute'
                    )
                )
            }

            if (slaData.resolutionTimeMinutes) {
                setValue(
                    'ticket_estimated_completion_date',
                    dayjs(requestedAt).add(
                        slaData.resolutionTimeMinutes,
                        'minute'
                    )
                )
                setValue(
                    'ticket_time_release',
                    dayjs().add(slaData.resolutionTimeMinutes, 'minute')
                )
                setSlaInfo({
                    time: slaData.resolutionTimeMinutes,
                    timeFormat: 'minute',
                    source: 'service_sla',
                })
            }
        },
        [watch, setValue]
    )

    // Handle ticket type group change
    const handleChangeTicketTypeGroup = (value: string) => {
        setValue('ticket_type_group', value)
        setValue('ticket_type_id', 0)
        setValue('ticket_type_key', '')
        setValue('ticket_content', '')
        setSlaInfo({})
    }

    // Handle ticket type change
    const handleChangeTicketType = (
        ticketType: Partial<
            TicketTypeResponse & { value: number; label: string }
        >
    ) => {
        const ticketTypeKey = ticketType?.ticket_type_key || ''

        setValue('ticket_type_id', ticketType.value)
        setValue('ticket_type_key', ticketTypeKey)

        const templateDefaults = getDefaults()
        const priority =
            ticketType?.ticket_priority ||
            (templateDefaults.ticket_priority as string) ||
            'low'
        setValue('ticket_priority', priority)

        const apiContent = ticketType?.ticket_type_sample_content || ''
        const templateContent = getSampleContent()
        setValue('ticket_content', apiContent || templateContent)

        if (ticketTypeKey === MEETING_TICKET_KEY) {
            setValue(
                'ticket_meeting_type',
                (templateDefaults.ticket_meeting_type as string) || 'online'
            )
            setValue(
                'ticket_meeting_channel',
                (templateDefaults.ticket_meeting_channel as string) || 'zoom'
            )
            setValue('ticket_meeting_via', '')
            setValue(
                'ticket_meeting_purpose',
                (templateDefaults.ticket_meeting_purpose as string) || 'support'
            )
        }

        if (serviceSLA.resolutionTimeMinutes) {
            setValue(
                'ticket_time_release',
                dayjs().add(serviceSLA.resolutionTimeMinutes, 'minute')
            )
        } else if (
            ticketType?.ticket_type_time &&
            ticketType?.ticket_type_time_format
        ) {
            const requestedAt = watch('ticket_requested_at') || dayjs()
            const slaTime = Number(ticketType?.ticket_type_time)
            const slaFormat = ticketType?.ticket_type_time_format as
                | 'minute'
                | 'hour'
                | 'day'

            setValue('ticket_time_release', dayjs().add(slaTime, slaFormat))
            setValue(
                'ticket_estimated_completion_date',
                dayjs(requestedAt).add(slaTime, slaFormat)
            )
            setSlaInfo({
                time: ticketType.ticket_type_time,
                timeFormat: ticketType.ticket_type_time_format,
                source: 'ticket_type',
            })
        } else {
            setValue('ticket_time_release', null)
            setValue('ticket_estimated_completion_date', null)
            setSlaInfo({})
        }
    }

    return {
        formControl,
        control,
        handleSubmit,
        watch,
        setValue,
        isPending,
        slaInfo,
        onSubmit,
        handleServiceChange,
        handleServiceSLALoaded,
        handleChangeTicketTypeGroup,
        handleChangeTicketType,
    }
}
